/**
 * Editor для doc.reconciliation_acts (Фаза 2.1 #4).
 * Workflow buttons + кнопка «Згенерувати з реєстрів».
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { reconciliationActs, type SnapshotRow, type TheirRow } from '@/api/settlements';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import RequiredLabel from '@/components/RequiredLabel';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';
import { useConfirm } from '@/components/ConfirmProvider';
import { accounts as accountsApi } from '@/api/documents';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { contractLabel } from '@/shared/utils/labels';
import TchTable from '@/shared/components/TchTable';

const PERMISSION = 'documents.reconciliation_acts.create';

interface FormState {
  organization_id: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  period_start: string;
  period_end: string;
  opening_balance_their: string;
  closing_balance_their: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  period_start: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
  period_end: new Date().toISOString().slice(0, 10),
  opening_balance_their: '',
  closing_balance_their: '',
  comment: '',
};

// Маршрути редакторів (п.5, 2026-08-19): рядок «наших даних» — посилання
// на сам документ. Типи без редактора лишаються текстом.
const DOC_TYPE_ROUTES: Record<string, string> = {
  'doc.goods_receipts': '/goods-receipts',
  'doc.goods_sales': '/goods-sales',
  'doc.bank_receipts': '/bank-receipts',
  'doc.bank_payments': '/bank-payments',
  'doc.cash_receipts': '/cash-receipts',
  'doc.cash_payments': '/cash-payments',
  'doc.goods_returns_from_customer': '/goods-returns-from-customer',
  'doc.goods_returns_to_supplier': '/goods-returns-to-supplier',
  'doc.manual_operations': '/manual-operations',
};

const DOC_TYPE_LABELS: Record<string, string> = {
  'doc.goods_receipts': 'Надходження',
  'doc.goods_sales': 'Реалізація',
  'doc.bank_receipts': 'Банк (надходження)',
  'doc.bank_payments': 'Банк (виплата)',
  'doc.cash_receipts': 'ПКО',
  'doc.cash_payments': 'РКО',
  'doc.goods_returns_from_customer': 'Повернення від покупця',
  'doc.goods_returns_to_supplier': 'Повернення постачальнику',
  'doc.debt_adjustments': 'Коригування боргу',
  'doc.manual_operations': 'Операція (бух. довідка)',
  'doc.additional_expenses': 'Дод. витрати',
};

const STATE_LABEL: Record<string, string> = {
  DRAFT: 'Чернетка', SENT: 'Відправлено', AGREED: 'Узгоджено', DISPUTED: 'Спірний',
};

const STATE_TONE: Record<string, 'gray' | 'blue' | 'green' | 'red'> = {
  DRAFT: 'gray', SENT: 'blue', AGREED: 'green', DISPUTED: 'red',
};

export default function ReconciliationActEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirmDlg = useConfirm();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const { availableOrgs } = usePermissionsByOrg(PERMISSION);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  // Вкладки як у 1С: наші дані / дані контрагента / рахунки обліку.
  const [tab, setTab] = useState<'ours' | 'theirs' | 'accounts'>('ours');
  // «За даними контрагента» — построкова таблиця (його Дт/Кт).
  const [theirRows, setTheirRows] = useState<TheirRow[]>([]);
  const [theirOpening, setTheirOpening] = useState('');
  // Рахунки звірки (вкладка «Рахунки обліку»).
  const [accCodes, setAccCodes] = useState<string[]>([]);
  const [newAcc, setNewAcc] = useState('');
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id, contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });
  const contractorName = (id: string | null | undefined) =>
    contractorsQ.data?.items.find((c) => c.id === id)?.name ?? '';

  const docQuery = useQuery({
    queryKey: ['reconciliation-act', id],
    queryFn: () => reconciliationActs.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  // Назви рахунків для вкладки «Рахунки обліку» (п.7, 2026-08-19).
  const accountsQ = useQuery({
    queryKey: ['accounts-recon'],
    queryFn: () => accountsApi.list({ chart_code: 'HOZRASCHETNY', limit: 2000 }),
    enabled: !isNew,
    staleTime: 10 * 60 * 1000,
  });
  const accName = (code: string): string => {
    const items = accountsQ.data ?? [];
    const exact = items.find((a) => a.code === code);
    if (exact) return exact.name;
    // код-префікс (36 → назва рахунку 36, або першого субрахунку 361)
    const pref = items.filter((a) => a.code.startsWith(code))
      .sort((a, b) => a.code.length - b.code.length)[0];
    return pref ? pref.name : '';
  };

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id ?? '',
        period_start: d.period_start,
        period_end: d.period_end,
        opening_balance_their: d.opening_balance_their ?? '',
        closing_balance_their: d.closing_balance_their ?? '',
        comment: d.comment ?? '',
      });
      setTheirRows(d.their_movements ?? []);
      // У БД сальдо контрагента — ДЗЕРКАЛО (наша система знаків);
      // у формі показуємо як у ЙОГО акті (raw = −збережене).
      setTheirOpening(d.opening_balance_their != null
        ? String(-Number(d.opening_balance_their)) : '');
      setAccCodes(d.accounts ?? ['36', '63', '371', '377', '681', '685']);
    }
  }, [isNew, docQuery.data]);

  const state = docQuery.data?.state ?? 'DRAFT';
  const isPosted = docQuery.data?.posted ?? false;

  const createFromRegisters = useMutation({
    mutationFn: () => reconciliationActs.fromRegisters({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      period_start: form.period_start,
      period_end: form.period_end,
      document_date: new Date(form.document_date).toISOString(),
      comment: form.comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['reconciliation-acts'] });
      navigate(`/settlements/reconciliation-acts/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const confirmFromContractor = useMutation({
    // 0437: построкові дані контрагента (як у його акті) — кінцеве сальдо
    // і дзеркальне збереження рахує сервер.
    mutationFn: () => reconciliationActs.confirmFromContractor(id!, {
      opening_their_raw: theirOpening || '0',
      their_movements: theirRows,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reconciliation-act', id] }),
    onError: (e) => setError(extractApiError(e)),
  });
  const regenerate = useMutation({
    mutationFn: () => reconciliationActs.regenerate(id!, accCodes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reconciliation-act', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const postDoc = useMutation({
    mutationFn: () => reconciliationActs.post(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reconciliation-act', id] }),
    onError: (e) => setError(extractApiError(e)),
  });
  const unpostDoc = useMutation({
    mutationFn: () => reconciliationActs.unpost(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reconciliation-act', id] }),
    onError: (e) => setError(extractApiError(e)),
  });
  const removeDoc = useMutation({
    mutationFn: () => reconciliationActs.remove(id!),
    onSuccess: () => navigate('/settlements/reconciliation-acts'),
    onError: (e) => setError(extractApiError(e)),
  });


  const handleGenerate = () => {
    setError(null);
    const err = validateRequired(
      { organization_id: form.organization_id, contractor_id: form.contractor_id },
      { organization_id: 'Юрособа', contractor_id: 'Контрагент' },
    );
    if (err) { setError(err); return; }
    createFromRegisters.mutate();
  };

  // ч.917 (новий дизайн, Закон #10): «ще дії» переїхали у праву колонку
  // (Повʼязані дії) — той самий набір, що був у extraActions тулбара.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [{
    // ч.1347: коригування — зі збереженого акта (сальдо рахується при створенні
    // з реєстрів), проведення не чекаємо.
    label: 'створити коригування боргу',
    disabled: isNew || Number(docQuery.data?.closing_balance_our ?? 0) === 0,
    title: isNew
      ? 'Спершу збережіть акт — коригування створюється на суму його сальдо'
      : Number(docQuery.data?.closing_balance_our ?? 0) === 0
        ? 'Сальдо нульове — коригувати нічого'
        : 'Створити документ коригування боргу на суму сальдо акта',
    onClick: () => {
      const bal = Number(docQuery.data!.closing_balance_our);
      const url = new URLSearchParams({
        organization_id: form.organization_id,
        adjustment_type: 'WRITE_OFF',
        debtor_contractor_id: form.contractor_id,
        ...(form.contract_id ? { debtor_contract_id: form.contract_id } : {}),
        debtor_account_code: bal > 0 ? '361' : '631',
        amount: Math.abs(bal).toFixed(2),
      });
      navigate(`/debt-adjustments/new?${url.toString()}`);
    },
  }];

  // ч.917: дані вкладок (були всередині IIFE) — підняті сюди, бо кнопки
  // таблиць тепер у шапці блоку. Звичайні const, не хуки.
  const d = docQuery.data;
  const snapshot: SnapshotRow[] =
    ((d?.movements_snapshot as SnapshotRow[] | null) ?? []).map((r) => (
      // Старі акти (до 0437): direction/amount без debit/credit.
      r.debit != null || r.credit != null ? r : {
        ...r,
        debit: r.direction === '+' ? r.amount : null,
        credit: r.direction === '-' ? r.amount : null,
      }));
  const fmt = (v: string | number | null | undefined) =>
    v == null || v === '' ? '' : Number(v).toLocaleString('uk-UA',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const oursDebit = snapshot.reduce((t, r) => t + (r.debit ? Number(r.debit) : 0), 0);
  const oursCredit = snapshot.reduce((t, r) => t + (r.credit ? Number(r.credit) : 0), 0);
  // Сторона контрагента — як у ЙОГО акті (його Дт/Кт).
  const thD = theirRows.reduce((t, r) => t + (r.debit ? Number(r.debit) : 0), 0);
  const thC = theirRows.reduce((t, r) => t + (r.credit ? Number(r.credit) : 0), 0);
  const thClose = Number(theirOpening || 0) + thD - thC;
  const editable = state !== 'AGREED' && !isPosted;
  const setRow = (i: number, patch: Partial<TheirRow>) =>
    setTheirRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const cellInput = { width: '100%', border: 'none', background: 'transparent',
    padding: '0.25rem 0.4rem', fontSize: '0.85rem' } as const;

  // ч.917: блок «Дані звірки» є лише у збереженого акта — нумерація з масиву.
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 1,
      filled: form.organization_id ? 1 : 0 },
    { id: 'contractor', title: 'Контрагент і період', required: 1,
      filled: form.contractor_id ? 1 : 0 },
    ...(!isNew ? [{ id: 'tables', title: 'Дані звірки', required: 0, filled: 0 }] : []),  // ч.917: дані генеруються з регістрів
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];
  const num = (bid: string) => blocks.findIndex((b) => b.id === bid) + 1;
  // ч.917: кнопки таблиць активної вкладки — у шапці блоку (умова та сама: editable).
  const tableActions = !editable ? undefined : tab === 'theirs' ? (
    <>
      <button type="button" className="btn-doc"
        title="Скопіювати наші рядки дзеркально (наш Дт → його Кт), як «Заполнить» у 1С"
        onClick={() => {
          setTheirRows(snapshot.map((r) => ({
            date: r.date,
            description: `${DOC_TYPE_LABELS[r.doc_type] ?? DOC_TABLE_NAMES_UK[r.doc_type] ?? r.doc_type}`
              + (r.doc_number ? ` № ${r.doc_number}` : ''),
            debit: r.credit ?? undefined,
            credit: r.debit ?? undefined,
          })));
          setTheirOpening(String(-Number(d?.opening_balance_our ?? 0)));
        }}>
        заповнити дзеркально з наших даних
      </button>
      <button type="button" className="primary"
        onClick={() => setTheirRows((rs) => [...rs, {}])}>
        + рядок
      </button>
    </>
  ) : tab === 'accounts' ? (
    <button type="button" className="btn-doc"
      disabled={regenerate.isPending || accCodes.length === 0}
      title="Перебудувати «наші дані» з проводок за цими рахунками"
      onClick={() => regenerate.mutate()}>
      {regenerate.isPending ? '…' : 'перегенерувати за цими рахунками'}
    </button>
  ) : undefined;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий акт звірки' : `Акт звірки ${docQuery.data?.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          railMode
          onBackToList={() => navigate('/settlements/reconciliation-acts')}
          savable={isNew}
          saveLabel="згенерувати з реєстрів"
          onSave={handleGenerate}
          savePending={createFromRegisters.isPending}
          isNew={isNew}
          isPosted={isPosted}
          onPost={() => postDoc.mutate()}
          // ч.1266: новий документ спершу формується кнопкою «зберегти» (з регістрів)
          postDisabled={isNew}
          postTitle={isNew ? 'Спершу збережіть документ' : undefined}
          postPending={postDoc.isPending}
          onUnpost={() => unpostDoc.mutate()}
          unpostPending={unpostDoc.isPending}
          onDelete={() => { confirmDlg('Видалити акт звірки?').then((ok) => { if (ok) removeDoc.mutate(); }); }}
          deletePending={removeDoc.isPending}
          onPrint={!isNew ? () => {
            const url = new URLSearchParams({
              organization_id: form.organization_id,
              contractor_id: form.contractor_id,
              ...(form.contract_id ? { contract_id: form.contract_id } : {}),
              date_from: form.period_start,
              date_to: form.period_end,
              act_id: id!,
            });
            navigate(`/reports/reconciliation-act/print?${url.toString()}`);
          } : undefined}
          entriesDoc={{ type: 'doc.reconciliation_acts', id: isNew ? '' : id! }}
          extraStatusBadges={!isNew && STATE_LABEL[state]
            ? [{ label: STATE_LABEL[state]!.toLowerCase(), tone: STATE_TONE[state] ?? 'gray' }] : []}
          extraActions={railActions}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.reconciliation_acts', id: isNew ? '' : id! }} />
      }>
      {/* ч.917 — блоками (Закон #10); сітка полів — як була. */}
      <DocBlock index={num('main')} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Юрособа</RequiredLabel>
          {isNew ? (
            <select value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
              ))}
            </select>
          ) : (
            <div>{orgList.data?.items.find((o) => o.id === form.organization_id)?.name ?? form.organization_id}</div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Дата документа</label>
          <DatePopover withTime value={form.document_date}
            disabled={!isNew}
            onChange={(v) => setForm({ ...form, document_date: v })} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={num('contractor')} title="Контрагент і період">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <RequiredLabel>Контрагент</RequiredLabel>
          {isNew ? (
            <SearchSelect
              items={(contractorsQ.data?.items ?? []).map((c) => ({
                id: c.id, label: c.name, hint: c.edrpou ?? null,
              }))}
              value={form.contractor_id}
              onChange={(id) => setForm({ ...form, contractor_id: id, contract_id: '' })}
            />
          ) : (
            <div style={{ fontWeight: 600 }}>{contractorName(form.contractor_id) || '…'}</div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Договір (опц.)</label>
          {isNew ? (
            <select value={form.contract_id} disabled={!form.contractor_id}
              onChange={(e) => setForm({ ...form, contract_id: e.target.value })}>
              <option value="">— всі договори —</option>
              {contractsQ.data?.map((c) => (
                <option key={c.id} value={c.id}>{contractLabel(c)}</option>
              ))}
            </select>
          ) : (
            <div>{contractsQ.data?.find((c) => c.id === form.contract_id)?.name ?? '— всі договори —'}</div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Період з</label>
          <DatePopover value={form.period_start}
            disabled={!isNew}
            onChange={(v) => setForm({ ...form, period_start: v })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>по</label>
          <DatePopover value={form.period_end}
            disabled={!isNew}
            onChange={(v) => setForm({ ...form, period_end: v })} />
        </div>
      </div>

      </DocBlock>

      {!isNew && (
      <DocBlock index={num('tables')} title="Дані звірки" actions={tableActions}>
          <div className="seg-tabs" style={{ marginBottom: '0.5rem' }}>
            {([['ours', 'За нашими даними'],
               ['theirs', 'За даними контрагента'],
               ['accounts', 'Рахунки обліку']] as const).map(([k, label]) => (
              <button key={k} type="button" className={tab === k ? 'active' : ''}
                onClick={() => setTab(k)}>{label}</button>
            ))}
            <span style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: '0.85rem',
              color: Number(d?.discrepancy_amount ?? 0) === 0 ? '#15803d' : '#b91c1c',
              fontWeight: 600 }}>
              {Number(d?.discrepancy_amount ?? 0) === 0
                ? 'розбіжності немає'
                : `розбіжність: ${fmt(d?.discrepancy_amount)} грн`}
            </span>
          </div>

          {tab === 'ours' && (
            <div>
              <TchTable>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                    <th style={{ width: 100 }}>Дата</th>
                    <th>Документ</th>
                    <th style={{ width: 130, textAlign: 'right' }}>Дебет</th>
                    <th style={{ width: 130, textAlign: 'right' }}>Кредит</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={2}>Сальдо початкове</td>
                    <td colSpan={2} style={{ textAlign: 'right' }}>
                      {fmt(d?.opening_balance_our)}
                    </td>
                  </tr>
                  {snapshot.map((r, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td>
                        {new Date(r.date).toLocaleDateString('uk-UA')}
                      </td>
                      <td>
                        {DOC_TYPE_ROUTES[r.doc_type] ? (
                          <a style={{ cursor: 'pointer' }}
                            title="Відкрити документ"
                            onClick={() => navigate(
                              `${DOC_TYPE_ROUTES[r.doc_type]}/${r.doc_id}`)}>
                            {(DOC_TYPE_LABELS[r.doc_type] ?? DOC_TABLE_NAMES_UK[r.doc_type] ?? r.doc_type)}
                            {r.doc_number ? ` № ${r.doc_number}` : ''}
                          </a>
                        ) : (
                          <>
                            {(DOC_TYPE_LABELS[r.doc_type] ?? DOC_TABLE_NAMES_UK[r.doc_type] ?? r.doc_type)}
                            {r.doc_number ? ` № ${r.doc_number}` : ''}
                          </>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmt(r.debit)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(r.credit)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '1px solid var(--border-strong)' }}>
                    <td colSpan={2}>Обороти за період</td>
                    <td style={{ textAlign: 'right' }}>{fmt(oursDebit)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(oursCredit)}</td>
                  </tr>
                  <tr style={{ fontWeight: 700 }}>
                    <td colSpan={2}>Сальдо кінцеве</td>
                    <td colSpan={2} style={{ textAlign: 'right' }}>
                      {fmt(d?.closing_balance_our)}
                    </td>
                  </tr>
                </tbody>
              </TchTable>
            </div>
          )}

          {tab === 'theirs' && (
            <div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end',
                flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                <div className="field" style={{ width: 220 }}>
                  <label>Сальдо початкове</label>
                  <input type="number" step="0.01" value={theirOpening}
                    disabled={!editable}
                    onChange={(e) => setTheirOpening(e.target.value)} />
                </div>
                {/* ч.917: «заповнити дзеркально» і «+ рядок» — у шапці блоку. */}
              </div>
              <TchTable>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                    <th style={{ width: 130 }}>Дата</th>
                    <th>Документ / опис</th>
                    <th style={{ width: 130 }}>Дебет</th>
                    <th style={{ width: 130 }}>Кредит</th>
                    {editable && <th style={{ width: 30 }} />}
                  </tr>
                </thead>
                <tbody>
                  {theirRows.length === 0 && (
                    <tr><td colSpan={5} style={{ color: 'var(--muted)' }}>
                      Немає рядків — додайте з акта контрагента або заповніть дзеркально.
                    </td></tr>
                  )}
                  {theirRows.map((r, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td>
                        {/* Закон дизайну #4: дати — через DatePopover. */}
                        <DatePopover value={r.date ?? ''} disabled={!editable}
                          onChange={(v) => setRow(i, { date: v })} />
                      </td>
                      <td><input style={cellInput} value={r.description ?? ''}
                        disabled={!editable} placeholder="напр. Надходження № 123"
                        onChange={(e) => setRow(i, { description: e.target.value })} /></td>
                      <td><input type="number" step="0.01" style={cellInput}
                        value={r.debit ?? ''} disabled={!editable}
                        onChange={(e) => setRow(i, { debit: e.target.value })} /></td>
                      <td><input type="number" step="0.01" style={cellInput}
                        value={r.credit ?? ''} disabled={!editable}
                        onChange={(e) => setRow(i, { credit: e.target.value })} /></td>
                      {editable && (
                        <td style={{ textAlign: 'center' }}>
                          <button style={{ border: 'none', background: 'none',
                            color: '#dc2626', cursor: 'pointer' }}
                            onClick={() => setTheirRows((rs) => rs.filter((_, x) => x !== i))}>
                            ×
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  <tr style={{ borderTop: '1px solid var(--border-strong)' }}>
                    <td colSpan={2}>Обороти за період</td>
                    <td>{fmt(thD)}</td>
                    <td>{fmt(thC)}</td>
                    {editable && <td />}
                  </tr>
                  <tr style={{ fontWeight: 700 }}>
                    <td colSpan={2}>
                      Сальдо кінцеве (за його даними)
                    </td>
                    <td colSpan={2}>{fmt(thClose)}</td>
                    {editable && <td />}
                  </tr>
                </tbody>
              </TchTable>
              {editable && (
                <button className="primary" style={{ marginTop: '0.5rem' }}
                  disabled={confirmFromContractor.isPending}
                  onClick={() => confirmFromContractor.mutate()}>
                  зберегти дані контрагента
                </button>
              )}
            </div>
          )}

          {tab === 'accounts' && (
            <div>
              <p style={{ color: 'var(--muted)', fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
                Рахунки, за якими будуються «наші дані» (з бухгалтерських
                проводок; код покриває і субрахунки — 36 включає 361, 362…).
              </p>
              <TchTable style={{ maxWidth: 560 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                    <th style={{ width: 90 }}>Рахунок</th>
                    <th>Найменування</th>
                    {editable && <th style={{ width: 30 }} />}
                  </tr>
                </thead>
                <tbody>
                  {accCodes.map((c) => (
                    <tr key={c} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ fontWeight: 600 }}>{c}</td>
                      <td>{accName(c) || '—'}</td>
                      {editable && (
                        <td style={{ textAlign: 'center' }}>
                          <button style={{ border: 'none', background: 'none',
                            color: '#dc2626', cursor: 'pointer' }}
                            title="Прибрати рахунок зі звірки"
                            onClick={() => setAccCodes((cs) => cs.filter((x) => x !== c))}>
                            ×
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </TchTable>
              {editable && (
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center',
                  marginTop: '0.5rem' }}>
                  <input style={{ width: 90 }} placeholder="код…" value={newAcc}
                    onChange={(e) => setNewAcc(e.target.value.replace(/[^0-9]/g, ''))} />
                  <button disabled={!newAcc}
                    onClick={() => {
                      if (newAcc && !accCodes.includes(newAcc)) {
                        setAccCodes((cs) => [...cs, newAcc]);
                      }
                      setNewAcc('');
                    }}>додати</button>
                </div>
              )}
              {/* ч.917: «перегенерувати за цими рахунками» — у шапці блоку. */}
            </div>
          )}
      </DocBlock>
      )}

      <DocBlock index={num('extra')} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          {/* ч.917: поля коментаря на формі не було (значення вже йшло у
              «згенерувати з реєстрів»); редагується лише у нового акта —
              окремого збереження шапки документ не має. */}
          <input value={form.comment} disabled={!isNew}
            onChange={(e) => setForm({ ...form, comment: e.target.value })} />
        </div>
        <div>
          {/* 0438 (п.5): загальний механізм — вкладення (PDF друкованої форми,
              скани) → журнал ЕДО: підписати, надіслати на підпис, статус ЕЦП. */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.reconciliation_acts" documentId={isNew ? undefined : id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: 'var(--badge-red-fg)' }}>{error}</div>
      )}
    </div>
  );
}
